import { PRIVACY_CLASSNAMES } from "@/lib/analytics/shared";
import { classNames, cn } from "@/lib/utils";
import { SectionHeader } from "@components/library/SectionHeader";

/** This is a UI block for showing the content of a message, or of a completion.
 *
 * It has content, and then it has spaces for role and controls, which stack on mobile.
 * The intention is that this same responsive layout should be used in playground, and the drawers.
 */
export const ContentRow = ({
  role,
  controls,
  children,
}: {
  role: React.ReactNode;
  controls?: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <div className={"group relative w-full gap-4 @container"}>
      <div className="relative flex w-full  flex-col px-20 @lg:flex-row @lg:items-start">
        {/* Narrow screens - stacked role and controls on top of the content */}
        <header className="flex h-20 items-center justify-between truncate @lg:hidden">
          {role}
          <div className="flex flex-row">{controls}</div>
        </header>

        {/* Wide - Role to the LHS */}
        <aside className="mr-8 hidden w-96 shrink-0 truncate @lg:block">{role}</aside>

        {/* Content */}
        {/* The -ml-4 is a bit of an inelegant hack to make the content line visually up with the role on narrow screens. */}
        <div className={classNames("relative -ml-4 flex grow flex-col @lg:-mt-[10px]", PRIVACY_CLASSNAMES["moderate"])}>
          {children}
        </div>

        {/* Wide  - Controls to the RHS */}
        <aside className="-mt-[4px] hidden w-[74px] justify-end @lg:flex">{controls}</aside>
      </div>
    </div>
  );
};

/** Goes above the content in Editor, like 'Chat', 'Inputs' etc. */
export const TitleRow = ({
  title,
  controls,
  children,
}: {
  title: React.ReactNode;
  controls?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  return (
    <div className={cn("group relative flex h-40 w-full items-center gap-4 @container")}>
      <div className="relative flex w-full flex-row items-center px-16 ">
        <SectionHeader actionsTray={controls} title={title} />

        <div className={"relative flex grow flex-col"}>{children}</div>
        <aside className="justify-end">{controls}</aside>
      </div>
    </div>
  );
};

/** Goes above Title e.g. for the sessions part of Editor. Should be the same but no truncation. */
export const SectionTitleRow = ({ title, controls }: { title: React.ReactNode; controls?: React.ReactNode }) => {
  return (
    <div className={cn("group relative flex h-64 w-full items-center gap-4 @container")}>
      <div className="relative flex w-full items-center justify-between px-16 ">
        <header className="shrink-0">{title}</header>
        <div />
        <aside className="justify-end">{controls}</aside>
      </div>
    </div>
  );
};
